import { IPlannedExecutionApi } from '@interfaces/campaign-analytics-api.interface';
import { TranslateService } from '@ngx-translate/core';
import { differenceInCalendarDays, isSameDay, isSameYear } from 'date-fns';
import { DATE_FORMAT, DateUtilitiesService } from './date-utilities.service';

const TIME_FRAME_TYPE_LABEL_KEY = 'shared.executionTypes.timeFrame';

// A planned time_frame's window as a label, plus "(n.)" when it asks for more than one execution -
// the same N identical planned nodes undesignated numbers that way. Dates only: a window counts in whole days.
export function getTimeFrameLabel(
  plannedExecution: Pick<IPlannedExecutionApi, 'executionCountTarget' | 'plannedEnd' | 'plannedStart'>,
  executionOrder: number | null,
  translate: TranslateService,
  dateUtils: DateUtilitiesService,
): string {
  // Planned executions from the worker carry no window until it ships planned_start / planned_end.
  if (!plannedExecution.plannedStart || !plannedExecution.plannedEnd) {
    return translate.instant(TIME_FRAME_TYPE_LABEL_KEY) as string;
  }

  const translationKey = executionOrder && plannedExecution.executionCountTarget > 1 ? 'timeFrameOrder' : 'timeFrame';

  return translate.instant(`pages.campaignDetails.deliverables.table.date.${translationKey}`, {
    end: dateUtils.formatDate(dateUtils.parseDateTimeFromApi(plannedExecution.plannedEnd), DATE_FORMAT),
    executionOrder,
    start: dateUtils.formatDate(dateUtils.parseDateTimeFromApi(plannedExecution.plannedStart), DATE_FORMAT),
  });
}

const SHORT_DATE_FORMAT = 'dd.MM';

// Tile variant of getTimeFrameLabel: drops whatever repeats (the year, the date of a single-day window) so the
// label fits in one line of a tile header.
export function getCompactTimeFrameLabel(
  plannedExecution: Pick<IPlannedExecutionApi, 'executionCountTarget' | 'plannedEnd' | 'plannedStart'>,
  executionOrder: number | null,
  translate: TranslateService,
  dateUtils: DateUtilitiesService,
): string {
  if (!plannedExecution.plannedStart || !plannedExecution.plannedEnd) {
    return translate.instant(TIME_FRAME_TYPE_LABEL_KEY) as string;
  }

  const start = dateUtils.parseDateTimeFromApi(plannedExecution.plannedStart);
  const end = dateUtils.parseDateTimeFromApi(plannedExecution.plannedEnd);
  const orderSuffix = executionOrder && plannedExecution.executionCountTarget > 1 ? ` (${executionOrder}.)` : '';

  return `${formatCompactWindow(start, end, translate, dateUtils)}${orderSuffix}`;
}

export function getRelativeDayLabel(date: Date, translate: TranslateService): string | null {
  const diffDays = differenceInCalendarDays(date, new Date());

  if (diffDays === -1) return translate.instant('shared.tile.yesterday') as string;

  if (diffDays === 0) return translate.instant('shared.tile.today') as string;

  if (diffDays === 1) return translate.instant('shared.tile.tomorrow') as string;

  return null;
}

function formatCompactWindow(
  start: Date,
  end: Date,
  translate: TranslateService,
  dateUtils: DateUtilitiesService,
): string {
  if (isSameDay(start, end)) {
    return getRelativeDayLabel(start, translate) ?? dateUtils.formatDate(start, DATE_FORMAT);
  }

  if (!isSameYear(start, end)) {
    return `${dateUtils.formatDate(start, DATE_FORMAT)} – ${dateUtils.formatDate(end, DATE_FORMAT)}`;
  }

  return `${dateUtils.formatDate(start, SHORT_DATE_FORMAT)} – ${dateUtils.formatDate(end, DATE_FORMAT)}`;
}
